"use client";

import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { generateEligibilityInformationPdf } from "@/lib/pdfme/eligibility-information-pdf";
import { cn } from "@/lib/utils";
import { getInsuranceCardDisplayName } from "@/server/models/insurance-number/insurance-card-name";
import type { Insurer } from "@/server/models/insurer/insurer";
import { formatDate } from "@/server/utils/format-date";
import { formatInsuranceNumberForDisplay } from "@/utils/format-insurance-number";
import { logger } from "@/utils/logger";
import { Font } from "@pdfme/common";
import Encoding from "encoding-japanese";
import { useState } from "react";
import { MdAutorenew } from "react-icons/md";
import type { InsuranceHistoryData } from "./types";

export const DownloadEligibilityInformationButton = ({
  insuranceNumber,
  primaryMember,
  insurer,
  children,
  className,
  disabled,
}: {
  insuranceNumber: InsuranceHistoryData;
  primaryMember: InsuranceHistoryData["primaryMember"];
  insurer: Insurer;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSubmitting(true);
    try {
      // フォント
      const notoSansJpRegular = await fetch(
        "/fonts/Noto_Sans_JP/static/NotoSansJP-Regular.ttf",
      );
      const notoSansJpBold = await fetch(
        "/fonts/Noto_Sans_JP/static/NotoSansJP-Bold.ttf",
      );
      const font = {
        noto_sans_jp_regular: {
          data: await notoSansJpRegular.arrayBuffer(),
          fallback: true,
        },
        noto_sans_jp_bold: {
          data: await notoSansJpBold.arrayBuffer(),
          fallback: false,
        },
      } satisfies Font;

      const [officeCode, insuranceCode, branchCode] =
        formatInsuranceNumberForDisplay(insuranceNumber.number).split("-");
      const nameKana = Encoding.toZenkanaCase(insuranceNumber.nameKana ?? "");
      const downloadedAt = formatDate(undefined, "YYYY年M月D日");
      const name = getInsuranceCardDisplayName({
        insuranceCardName: insuranceNumber.insuranceCardName,
        name: insuranceNumber.name,
      });
      const primaryMemberName =
        insuranceNumber.type === "dependent"
          ? getInsuranceCardDisplayName({
              insuranceCardName: primaryMember?.insuranceCardName,
              name: primaryMember?.name ?? "",
            })
          : name;

      const pdf = await generateEligibilityInformationPdf({
        input: {
          topDownloadedAt: formatDate(undefined, "YYYY年M月D日"),
          topOfficeCode: officeCode,
          topInsuranceCode: `${insuranceCode}　（枝番）${branchCode}`,
          topNameOfPrimary: `${primaryMemberName} 様`,
          topName: `${name} 様`,
          tableOfficeCode: officeCode,
          tableInsuranceCode: `${insuranceCode}　（枝番）${branchCode}`,
          tableName: name,
          tableNameKana: nameKana,
          tableDateOfBirth: formatDate(
            insuranceNumber.dateOfBirth,
            "YYYY年M月D日",
          ),
          tableAcquiredAt: formatDate(
            insuranceNumber.acquiredAt,
            "YYYY年M月D日",
          ),
          tableHealthcareSocietyName: insurer.name,
          tableCoPaymentRatio:
            insuranceNumber.elderlyBenefitsCardStatus === "issued"
              ? `${Number(insuranceNumber.elderlyBenefitsCardCopaymentRate) * 10}割（${downloadedAt}時点）`
              : "",
          cardOfficeCode: officeCode,
          cardInsuranceCode: insuranceCode,
          cardBranchCode: branchCode,
          cardNameKana: nameKana,
          cardName: name,
          cardDateOfBirth: formatDate(
            insuranceNumber.dateOfBirth,
            "YYYY年M月D日",
          ),
          cardAcquiredAt: formatDate(
            insuranceNumber.acquiredAt,
            "YYYY年M月D日",
          ),
          cardHealthcareSocietyNumber: insurer.insurerNumber,
          cardHealthcareSocietyName: insurer.name,
        },
        font,
      });

      // @ts-expect-error pdfmeは Uint8Array<ArrayBufferLike> （ArrayBufferLikeのビュー）を返すが、
      // Blobのコンストラクタは ArrayBuffer（もしくはそのビュー）を要求しておりインターフェースを満たせない
      // 挙動は問題ないので型エラーを無視する
      const blob = new Blob([pdf], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "資格情報のお知らせ.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast({
        title: "ダウンロードが完了しました",
        variant: "success",
      });
    } catch (error) {
      logger.error({ error }, "Download eliigibility information failed");
      toast({
        title: "ダウンロードに失敗しました",
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Button
      variant="primary"
      className={cn("flex items-center justify-center", className)}
      disabled={disabled || submitting}
      onClick={handleClick}
    >
      <span className="relative flex items-center">
        <MdAutorenew
          className={cn(
            "absolute -left-6 h-4 w-4",
            submitting ? "animate-spin opacity-100" : "opacity-0",
          )}
        />
        {children}
      </span>
    </Button>
  );
};
